// // ─────────────────────────────────────────────────────────────
// // usePricing — single source of truth for all price display
// //
// // Fetches live USD price from server, converts to user's local
// // currency using exchangerate-api.com (cached 1hr in sessionStorage)
// // with hardcoded fallback rates if the API is unavailable.
// //
// // Usage:
// //   const { usdDisplay, localDisplay, fullDisplay, loading } = usePricing();
// //   usdDisplay  → "$4.99"
// //   localDisplay→ "₹419" (null for USD regions)
// //   fullDisplay → "$4.99 (≈₹419)"
// //
// // Drop-in <PriceTag /> component for anywhere a price is shown.
// // ─────────────────────────────────────────────────────────────
// import { useState, useEffect } from "react";

// const BACKEND      = process.env.REACT_APP_BACKEND_URL || "https://ats-app-795v.onrender.com";
// const CACHE_KEY    = "fx_usd_rates_v1";
// const CACHE_TTL_MS = 60 * 60 * 1000;

// const USD_REGIONS = new Set([
//   "US","AS","EC","PR","SV","GU","MH","FM","MP","PW","TC","VI","ZW","BZ","PA",
// ]);

// const FALLBACK_RATES = {
//   INR:84, GBP:0.79, AUD:1.53, CAD:1.36, EUR:0.92, JPY:150, SGD:1.34,
//   AED:3.67, ZAR:18.5, BRL:5.0, MXN:17.5, NZD:1.65, CHF:0.90, HKD:7.83,
//   SEK:10.5, NOK:10.6, DKK:6.87, PLN:3.95, CZK:23.0, HUF:358, TRY:32.5,
//   MYR:4.72, THB:35.5, PHP:58.0, IDR:15750, VND:25300, KRW:1340, TWD:31.9,
//   PKR:278, BDT:110, LKR:303, NGN:1560, KES:130, GHS:15.5, MAD:9.97,
//   EGP:48.5, SAR:3.75, QAR:3.64, KWD:0.307, OMR:0.385, BHD:0.377, JOD:0.709,
// };

// const REGION_CURRENCY = {
//   IN:"INR", PK:"PKR", BD:"BDT", LK:"LKR",
//   GB:"GBP", DE:"EUR", FR:"EUR", IT:"EUR", ES:"EUR", NL:"EUR", BE:"EUR",
//   PT:"EUR", AT:"EUR", IE:"EUR", FI:"EUR", GR:"EUR", LU:"EUR", SK:"EUR",
//   SI:"EUR", EE:"EUR", LV:"EUR", LT:"EUR", MT:"EUR", CY:"EUR",
//   CH:"CHF", SE:"SEK", NO:"NOK", DK:"DKK", PL:"PLN", CZ:"CZK", HU:"HUF",
//   AU:"AUD", NZ:"NZD", JP:"JPY", SG:"SGD", HK:"HKD", KR:"KRW", TW:"TWD",
//   MY:"MYR", TH:"THB", PH:"PHP", ID:"IDR", VN:"VND",
//   AE:"AED", SA:"SAR", QA:"QAR", KW:"KWD", OM:"OMR", BH:"BHD",
//   JO:"JOD", EG:"EGP", MA:"MAD", NG:"NGN", KE:"KES", GH:"GHS", ZA:"ZAR",
//   CA:"CAD", BR:"BRL", MX:"MXN", TR:"TRY",
// };

// function detectRegion() {
//   // ── TESTING: temporarily force a region to test local currency display.
//   // Set TEST_REGION to any country code e.g. "IN", "GB", "AU", "AE"
//   // Set to null or "" to use the real browser locale.
//   const TEST_REGION = null; // e.g. "IN" to test Indian Rupee

//   if (TEST_REGION) {
//     const localeMap = {
//       IN:"en-IN", GB:"en-GB", AU:"en-AU", CA:"en-CA",
//       DE:"de-DE", AE:"ar-AE", JP:"ja-JP", SG:"en-SG",
//     };
//     return { locale: localeMap[TEST_REGION] || "en-US", region: TEST_REGION };
//   }

//   const langs  = navigator.languages?.length ? navigator.languages : [navigator.language || "en-US"];
//   const locale = langs[0] || "en-US";
//   const parts  = locale.split("-");
//   return { locale, region: (parts[parts.length - 1] || "").toUpperCase() };
// }

// async function getRate(currency) {
//   try {
//     const cached = sessionStorage.getItem(CACHE_KEY);
//     if (cached) {
//       const { rates, ts } = JSON.parse(cached);
//       if (Date.now() - ts < CACHE_TTL_MS && rates[currency]) return rates[currency];
//     }
//     const res  = await fetch("https://api.exchangerate-api.com/v4/latest/USD", {
//       signal: AbortSignal.timeout(5000),
//     });
//     const data = await res.json();
//     sessionStorage.setItem(CACHE_KEY, JSON.stringify({ rates: data.rates, ts: Date.now() }));
//     return data.rates[currency] || FALLBACK_RATES[currency] || null;
//   } catch {
//     return FALLBACK_RATES[currency] || null;
//   }
// }

// function formatLocal(usdAmount, rate, currency, locale) {
//   try {
//     return new Intl.NumberFormat(locale, {
//       style: "currency", currency, maximumFractionDigits: 0,
//     }).format(Math.round(usdAmount * rate));
//   } catch {
//     return `${currency} ${Math.round(usdAmount * rate)}`;
//   }
// }

// // ─────────────────────────────────────────────
// // usePricing hook
// // ─────────────────────────────────────────────
// export function usePricing(planId = "ats_basic") {
//   const [usdCents,     setUsdCents]     = useState(null);
//   const [usdDisplay,   setUsdDisplay]   = useState("...");
//   const [localDisplay, setLocalDisplay] = useState(null);
//   const [loading,      setLoading]      = useState(true);

//   useEffect(() => {
//     let cancelled = false;
//     async function run() {
//       // Step 1 — fetch live USD price from server
//       let cents   = planId === "doc_pro" ? 699 : 499;
//       let display = planId === "doc_pro" ? "$6.99" : "$4.99";
//       try {
//         const res  = await fetch(`${BACKEND}/config`);
//         const data = await res.json();
//         if (data.plans?.[planId]?.priceInCents) {
//           cents   = data.plans[planId].priceInCents;
//           display = data.plans[planId].priceDisplay || `$${(cents / 100).toFixed(2)}`;
//         }
//       } catch { /* use defaults */ }

//       if (cancelled) return;
//       setUsdCents(cents);
//       setUsdDisplay(display);

//       // Step 2 — detect region and convert
//       const { locale, region } = detectRegion();
//       if (!region || USD_REGIONS.has(region)) {
//         setLocalDisplay(null);
//         setLoading(false);
//         return;
//       }
//       const currency = REGION_CURRENCY[region];
//       if (!currency) { setLocalDisplay(null); setLoading(false); return; }

//       const rate = await getRate(currency);
//       if (cancelled) return;
//       if (rate) setLocalDisplay(formatLocal(cents / 100, rate, currency, locale));
//       setLoading(false);
//     }
//     run();
//     return () => { cancelled = true; };
//   }, [planId]);

//   const fullDisplay = localDisplay
//     ? `${usdDisplay} (≈${localDisplay})`
//     : usdDisplay;

//   return { usdCents, usdDisplay, localDisplay, fullDisplay, loading };
// }

// // ─────────────────────────────────────────────
// // PriceTag — drop-in display component
// // <PriceTag />          → "$4.99 (≈₹419)"
// // <PriceTag compact />  → "$4.99" + small local badge
// // ─────────────────────────────────────────────
// export function PriceTag({ planId = "ats_basic", compact = false, style = {} }) {
//   const { usdDisplay, localDisplay, loading } = usePricing(planId);

//   if (loading) return <span style={{ opacity:0.5, ...style }}>{usdDisplay === "..." ? "…" : usdDisplay}</span>;
//   if (!localDisplay) return <span style={style}>{usdDisplay}</span>;

//   if (compact) {
//     return (
//       <span style={{ display:"inline-flex", alignItems:"baseline", gap:5, ...style }}>
//         <span>{usdDisplay}</span>
//         <span style={{ fontSize:"0.7em", fontWeight:500, color:"#6b7280", background:"#f3f4f6", padding:"1px 6px", borderRadius:10 }}>
//           ≈{localDisplay}
//         </span>
//       </span>
//     );
//   }

//   return (
//     <span style={style}>
//       {usdDisplay}{" "}
//       <span style={{ opacity:0.65, fontSize:"0.85em" }}>(≈{localDisplay})</span>
//     </span>
//   );
// }

// // ─────────────────────────────────────────────
// // ModalPriceDisplay — for PaymentModal
// // Shows USD large, local estimate below
// // ─────────────────────────────────────────────
// export function ModalPriceDisplay({ usdDisplay, localDisplay, dark = false }) {
//   const textColor  = dark ? "#fff"                  : "#0f172a";
//   const mutedColor = dark ? "rgba(255,255,255,0.55)" : "#64748b";
//   const badgeBg    = dark ? "rgba(255,255,255,0.12)" : "#f1f5f9";
//   const badgeColor = dark ? "rgba(255,255,255,0.7)"  : "#475569";

//   return (
//     <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
//       <div style={{ fontSize:40, fontWeight:800, color:textColor, lineHeight:1, letterSpacing:"-0.03em" }}>
//         {usdDisplay}
//       </div>
//       <div style={{ fontSize:12, color:mutedColor }}>one-time · no subscription</div>
//       {localDisplay && (
//         <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:6, background:badgeBg, borderRadius:20, padding:"4px 12px" }}>
//           <span style={{ fontSize:11, color:badgeColor }}>≈ {localDisplay} in your currency</span>
//           <span style={{ fontSize:10, color:mutedColor }} title="Charged in USD. Local estimate for reference only.">ⓘ</span>
//         </div>
//       )}
//     </div>
//   );
// }

// // ─────────────────────────────────────────────
// // StripeLocalNote — below Stripe payment form
// // Explains USD charge vs local estimate
// // ─────────────────────────────────────────────
// export function StripeLocalNote({ usdDisplay, localDisplay }) {
//   if (!localDisplay) return null;
//   return (
//     <div style={{ marginTop:12, padding:"10px 14px", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, fontSize:12, color:"#92400e", lineHeight:1.6 }}>
//       <strong>Note:</strong> Stripe charges in USD ({usdDisplay}). Your bank will convert to your
//       local currency. The ≈{localDisplay} shown is an estimate based on today's rate and may
//       differ slightly from your final charge.
//     </div>
//   );
// }

// new code
// ─────────────────────────────────────────────────────────────
// usePricing — single source of truth for all price display
//
// Fetches live USD price from server, converts to user's local
// currency using exchangerate-api.com (cached 6hr in localStorage)
// with hardcoded fallback rates if the API is unavailable.
//
// Performance improvements:
//   • /config result cached in localStorage for 1 hour
//   • Exchange rates cached in localStorage for 6 hours (was sessionStorage 1hr)
//   • 2s timeout on /config — falls back to hardcoded price instantly
//   • 3s timeout on exchange rate API — falls back to hardcoded rates
//   • Both fetches run in parallel (Promise.all)
//   • No fetch at all if both caches are fresh
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "https://ats-app-795v.onrender.com";

// ── Cache keys + TTLs ──
const FX_CACHE_KEY     = "rsai_fx_rates_v2";
const FX_CACHE_TTL_MS  = 6 * 60 * 60 * 1000;  // 6 hours — rates don't change meaningfully

const CFG_CACHE_KEY    = "rsai_config_v1";
const CFG_CACHE_TTL_MS = 60 * 60 * 1000;       // 1 hour — price changes are rare

// ── Hardcoded fallback prices — used if /config is unreachable ──
const FALLBACK_PRICES = {
  ats_basic: { priceInCents: 499, priceDisplay: "$4.99" },
  doc_pro:   { priceInCents: 699, priceDisplay: "$6.99" },
};

const USD_REGIONS = new Set([
  "US","AS","EC","PR","SV","GU","MH","FM","MP","PW","TC","VI","ZW","BZ","PA",
]);

const FALLBACK_RATES = {
  INR:84,   GBP:0.79,  AUD:1.53, CAD:1.36, EUR:0.92,  JPY:150,  SGD:1.34,
  AED:3.67, ZAR:18.5,  BRL:5.0,  MXN:17.5, NZD:1.65,  CHF:0.90, HKD:7.83,
  SEK:10.5, NOK:10.6,  DKK:6.87, PLN:3.95, CZK:23.0,  HUF:358,  TRY:32.5,
  MYR:4.72, THB:35.5,  PHP:58.0, IDR:15750, VND:25300, KRW:1340, TWD:31.9,
  PKR:278,  BDT:110,   LKR:303,  NGN:1560, KES:130,   GHS:15.5, MAD:9.97,
  EGP:48.5, SAR:3.75,  QAR:3.64, KWD:0.307, OMR:0.385, BHD:0.377, JOD:0.709,
};

const REGION_CURRENCY = {
  IN:"INR", PK:"PKR", BD:"BDT", LK:"LKR",
  GB:"GBP", DE:"EUR", FR:"EUR", IT:"EUR", ES:"EUR", NL:"EUR", BE:"EUR",
  PT:"EUR", AT:"EUR", IE:"EUR", FI:"EUR", GR:"EUR", LU:"EUR", SK:"EUR",
  SI:"EUR", EE:"EUR", LV:"EUR", LT:"EUR", MT:"EUR", CY:"EUR",
  CH:"CHF", SE:"SEK", NO:"NOK", DK:"DKK", PL:"PLN", CZ:"CZK", HU:"HUF",
  AU:"AUD", NZ:"NZD", JP:"JPY", SG:"SGD", HK:"HKD", KR:"KRW", TW:"TWD",
  MY:"MYR", TH:"THB", PH:"PHP", ID:"IDR", VN:"VND",
  AE:"AED", SA:"SAR", QA:"QAR", KW:"KWD", OM:"OMR", BH:"BHD",
  JO:"JOD", EG:"EGP", MA:"MAD", NG:"NGN", KE:"KES", GH:"GHS", ZA:"ZAR",
  CA:"CAD", BR:"BRL", MX:"MXN", TR:"TRY",
};

// ── Safe localStorage helpers — never throws ──
function lsGet(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}
function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// ── Detect region from browser locale ──
function detectRegion() {
  const TEST_REGION = null; // set to "IN", "GB" etc to test locally

  if (TEST_REGION) {
    const localeMap = {
      IN:"en-IN", GB:"en-GB", AU:"en-AU", CA:"en-CA",
      DE:"de-DE", AE:"ar-AE", JP:"ja-JP", SG:"en-SG",
    };
    return { locale: localeMap[TEST_REGION] || "en-US", region: TEST_REGION };
  }

  const langs  = navigator.languages?.length ? navigator.languages : [navigator.language || "en-US"];
  const locale = langs[0] || "en-US";
  const parts  = locale.split("-");
  return { locale, region: (parts[parts.length - 1] || "").toUpperCase() };
}

// ── Fetch /config with cache + 2s timeout ──
async function fetchConfig() {
  // Return from cache if fresh
  const cached = lsGet(CFG_CACHE_KEY);
  if (cached?.data && Date.now() - cached.ts < CFG_CACHE_TTL_MS) {
    return cached.data;
  }

  try {
    const res  = await fetch(`${BACKEND}/config`, {
      signal: AbortSignal.timeout(2000), // 2s max — fall back to hardcoded if slow
    });
    const data = await res.json();
    lsSet(CFG_CACHE_KEY, { data, ts: Date.now() });
    return data;
  } catch {
    // Render cold start or network issue — use hardcoded fallback immediately
    return null;
  }
}

// ── Fetch exchange rate with cache + 3s timeout ──
async function getRate(currency) {
  // Return from cache if fresh
  const cached = lsGet(FX_CACHE_KEY);
  if (cached?.rates && Date.now() - cached.ts < FX_CACHE_TTL_MS) {
    return cached.rates[currency] || FALLBACK_RATES[currency] || null;
  }

  try {
    const res  = await fetch("https://api.exchangerate-api.com/v4/latest/USD", {
      signal: AbortSignal.timeout(3000), // 3s max — use fallback rates if slow
    });
    const data = await res.json();
    lsSet(FX_CACHE_KEY, { rates: data.rates, ts: Date.now() });
    return data.rates[currency] || FALLBACK_RATES[currency] || null;
  } catch {
    return FALLBACK_RATES[currency] || null; // always returns something
  }
}

function formatLocal(usdAmount, rate, currency, locale) {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency", currency, maximumFractionDigits: 0,
    }).format(Math.round(usdAmount * rate));
  } catch {
    return `${currency} ${Math.round(usdAmount * rate)}`;
  }
}

// ─────────────────────────────────────────────
// usePricing hook
// ─────────────────────────────────────────────
export function usePricing(planId = "ats_basic") {
  // ── Start with hardcoded fallback so UI renders immediately ──
  // Computed outside state/effect so ESLint doesn't complain about deps
  const fallbackCents   = (FALLBACK_PRICES[planId] || FALLBACK_PRICES.ats_basic).priceInCents;
  const fallbackDisplay = (FALLBACK_PRICES[planId] || FALLBACK_PRICES.ats_basic).priceDisplay;

  const [usdCents,     setUsdCents]     = useState(fallbackCents);
  const [usdDisplay,   setUsdDisplay]   = useState(fallbackDisplay);
  const [localDisplay, setLocalDisplay] = useState(null);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const { locale, region } = detectRegion();
      const isUsdRegion = !region || USD_REGIONS.has(region);
      const currency    = isUsdRegion ? null : REGION_CURRENCY[region];

      // ── Run config + exchange rate fetch in parallel ──
      const [configData, rate] = await Promise.all([
        fetchConfig(),
        currency ? getRate(currency) : Promise.resolve(null),
      ]);

      if (cancelled) return;

      // ── Update USD price from server (or keep fallback) ──
      let cents   = fallbackCents;
      let display = fallbackDisplay;

      if (configData?.plans?.[planId]?.priceInCents) {
        cents   = configData.plans[planId].priceInCents;
        display = configData.plans[planId].priceDisplay || `$${(cents / 100).toFixed(2)}`;
      }

      setUsdCents(cents);
      setUsdDisplay(display);

      // ── Set local currency display ──
      if (currency && rate) {
        setLocalDisplay(formatLocal(cents / 100, rate, currency, locale));
      }

      setLoading(false);
    }

    run();
    return () => { cancelled = true; };
  }, [planId, fallbackCents, fallbackDisplay]); // eslint-disable-line react-hooks/exhaustive-deps

  const fullDisplay = localDisplay
    ? `${usdDisplay} (≈${localDisplay})`
    : usdDisplay;

  return { usdCents, usdDisplay, localDisplay, fullDisplay, loading };
}

// ─────────────────────────────────────────────
// PriceTag — drop-in display component
// ─────────────────────────────────────────────
export function PriceTag({ planId = "ats_basic", compact = false, style = {} }) {
  const { usdDisplay, localDisplay, loading } = usePricing(planId);

  if (loading) return <span style={{ opacity: 0.5, ...style }}>{usdDisplay}</span>;
  if (!localDisplay) return <span style={style}>{usdDisplay}</span>;

  if (compact) {
    return (
      <span style={{ display: "inline-flex", alignItems: "baseline", gap: 5, ...style }}>
        <span>{usdDisplay}</span>
        <span style={{ fontSize: "0.7em", fontWeight: 500, color: "#6b7280", background: "#f3f4f6", padding: "1px 6px", borderRadius: 10 }}>
          ≈{localDisplay}
        </span>
      </span>
    );
  }

  return (
    <span style={style}>
      {usdDisplay}{" "}
      <span style={{ opacity: 0.65, fontSize: "0.85em" }}>(≈{localDisplay})</span>
    </span>
  );
}

// ─────────────────────────────────────────────
// ModalPriceDisplay — for PaymentModal
// ─────────────────────────────────────────────
export function ModalPriceDisplay({ usdDisplay, localDisplay, dark = false }) {
  const textColor  = dark ? "#fff"                   : "#0f172a";
  const mutedColor = dark ? "rgba(255,255,255,0.55)" : "#64748b";
  const badgeBg    = dark ? "rgba(255,255,255,0.12)" : "#f1f5f9";
  const badgeColor = dark ? "rgba(255,255,255,0.7)"  : "#475569";

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <div style={{ fontSize: 40, fontWeight: 800, color: textColor, lineHeight: 1, letterSpacing: "-0.03em" }}>
        {usdDisplay}
      </div>
      <div style={{ fontSize: 12, color: mutedColor }}>one-time · no subscription</div>
      {localDisplay && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 6, background: badgeBg, borderRadius: 20, padding: "4px 12px" }}>
          <span style={{ fontSize: 11, color: badgeColor }}>≈ {localDisplay} in your currency</span>
          <span style={{ fontSize: 10, color: mutedColor }} title="Charged in USD. Local estimate for reference only.">ⓘ</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// StripeLocalNote — below Stripe payment form
// ─────────────────────────────────────────────
export function StripeLocalNote({ usdDisplay, localDisplay }) {
  if (!localDisplay) return null;
  return (
    <div style={{ marginTop: 12, padding: "10px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
      <strong>Note:</strong> Stripe charges in USD ({usdDisplay}). Your bank will convert to your
      local currency. The ≈{localDisplay} shown is an estimate based on today's rate and may
      differ slightly from your final charge.
    </div>
  );
}
